const roots = document.querySelectorAll('.nt-advisor:not([data-initialized])');

roots.forEach((root) => {
  root.dataset.initialized = 'true';
  const form = root.querySelector('.nt-advisor__form');
  const config = {
    backendUrl: root.dataset.backendUrl.replace(/\/$/, ''),
    quoteEndpoint: root.dataset.quoteEndpoint || '/shipping-quote',
    pdfEndpoint: root.dataset.pdfEndpoint || '/generate-pdf',
    directCheckoutEndpoint: root.dataset.directCheckoutEndpoint || '/create-direct-checkout',
    requestEndpoint: root.dataset.requestEndpoint || '/create-advisor-request',
    liveQuote: root.dataset.enableLiveQuote === 'true',
    backendPdf: root.dataset.enableBackendPdf === 'true',
    checkoutEnabled: root.dataset.enableCheckout === 'true',
    density: Number(root.dataset.density) || 2700,
    palletCapacity: Number(root.dataset.palletCapacity) || 1000,
    pallet: {
      length: Number(root.dataset.palletLength) || 120,
      width: Number(root.dataset.palletWidth) || 80,
      height: Number(root.dataset.palletHeight) || 100,
    },
    origin: {
      postalCode: root.dataset.originPostcode,
      city: root.dataset.originCity,
      countryCode: root.dataset.originCountry,
    },
  };
  const data = JSON.parse(root.querySelector('.nt-advisor__data').textContent);
  const state = {
    step: 2, use: 'Boden', surface: 'Poliert', cutoutShape: '', tableShape: '', worktopShape: '', purchaseMode: 'direct', selected: new Map(), projects: [], transportQuote: null, transportQuoteSignature: '', transportLoading: false, transportPromise: null,
    sessionId: window.crypto?.randomUUID?.() || `nt-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    requestSubmitted: false,
  };
  const productContainer = root.querySelector('[data-products]');
  const errorBox = root.querySelector('[data-errors]');
  const taxRate = 0.19;
  const directMinimumAreaM2 = 10;

  // V8.7.3 Funnel-Tracking via Google Tag Manager dataLayer.
  // Es werden bewusst keine Namen, E-Mail-Adressen, Telefonnummern oder Anschriften übertragen.
  const advisorVersion = '8.7.4';
  // Keep existing event IDs stable; display the remaining four steps as 1–4.
  const firstStep = 2;
  const totalSteps = 4;
  const stepNames = { 1: 'use', 2: 'product', 3: 'execution', 4: 'summary', 5: 'contact' };
  const trackedStepViews = new Set();

  function trackAdvisorEvent(eventName, parameters = {}) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      mb_version: advisorVersion,
      mb_step: state.step,
      mb_step_index: state.step - firstStep + 1,
      mb_step_total: totalSteps,
      mb_step_name: stepNames[state.step] || '',
      mb_use: state.use || '',
      mb_purchase_mode: state.purchaseMode || '',
      ...parameters,
    });
  }

  const value = (name) => form.elements[name]?.value?.trim() || '';
  const checked = (name) => Boolean(form.elements[name]?.checked);
  const numberValue = (name) => Number(String(value(name)).replace(',', '.')) || 0;
  const escapeHtml = (input) => String(input ?? '').replace(/[&<>'"]/g, (char) => ({'&':'&amp;','<':'&lt;','>':'&gt;',"'":'&#39;','"':'&quot;'}[char]));
  const currency = (cents) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: data.currency || 'EUR' }).format((Number(cents) || 0) / 100);
  const grossFromNet = (cents) => Math.round((Number(cents) || 0) * (1 + taxRate));
  const decimal = (number, digits = 2) => new Intl.NumberFormat('de-DE', { maximumFractionDigits: digits }).format(number || 0);
  const productText = (product) => [product.title, product.vendor, product.product_type, ...(product.tags || []), ...(product.variants || []).map((variant) => variant.title)].join(' ').toLowerCase();
  const isDirectPurchaseProduct = (product) => (product.tags || []).some((tag) => String(tag).trim().toLowerCase() === 'direktkauf');
  const isDimensionUse = () => ['Arbeitsplatte', 'Fensterbank', 'Tisch', 'Waschtisch'].includes(state.use);
  const isDirectCheckoutUse = () => ['Boden', 'Wand'].includes(state.use);

  function transportQuoteSignature() {
    return JSON.stringify({
      postalCode: value('postalCode'),
      countryCode: value('countryCode') || 'DE',
      sample: checked('sample'),
      projects: allProjects().map((project) => ({
        use: project.use,
        surface: project.surface,
        billingUnits: project.billingUnits,
        metrics: project.metrics,
        items: project.items.map(({product, variant}) => ({productId: product.id, variantId: variant.id, price: Number(variant.price)})),
      })),
    });
  }

  function activeTransportQuote() {
    return state.transportQuote && state.transportQuoteSignature === transportQuoteSignature() ? state.transportQuote : null;
  }

  function currentProject() {
    const items = selectedItems();
    if (!state.use || !items.length) return null;
    const current = metrics();
    const customPrice = isDimensionUse();
    const requestOnly = customPrice || state.purchaseMode !== 'direct' || items.some(({product}) => !isDirectPurchaseProduct(product));
    const billingUnits = customPrice ? 1 : Math.max(1, Math.ceil(current.materialArea));
    return {
      use: state.use, surface: state.surface, customPrice, requestOnly, priceOnRequest: requestOnly,
      purchaseMode: requestOnly ? 'request' : 'direct',
      checkoutEligible: isDirectCheckoutUse() && !requestOnly,
      cutoutShape: state.cutoutShape, measurement: dimensionDescription(), metrics: {...current}, billingUnits,
      items: items.map(({product, variant}) => ({product, variant})),
    };
  }

  function allProjects() {
    const current = currentProject();
    return [...state.projects, ...(current ? [current] : [])];
  }

  function pricing() {
    const projects = allProjects();
    const totalWeightKg = projects.reduce((sum, project) => sum + project.metrics.weightKg, 0);
    const pallets = totalWeightKg > 0 ? Math.ceil(totalWeightKg / config.palletCapacity) : 0;
    const estimatedMaterialCents = projects.reduce((sum, project) => sum + project.items.reduce((itemSum, item) => itemSum + Number(item.variant.price) * project.billingUnits, 0), 0);
    const materialCents = projects.reduce((sum, project) => sum + (project.priceOnRequest ? 0 : project.items.reduce((itemSum, item) => itemSum + Number(item.variant.price) * project.billingUnits, 0)), 0);
    const palletCents = data.palletVariant ? Number(data.palletVariant.price) * pallets : 0;
    const sampleCents = checked('sample') && data.sampleVariant ? Number(data.sampleVariant.price) : 0;
    const freightCents = Number(activeTransportQuote()?.netPriceCents) || 0;
    const totalNetCents = materialCents + palletCents + sampleCents + freightCents;
    const vatCents = Math.round(totalNetCents * taxRate);
    const totalGrossCents = totalNetCents + vatCents;
    return {
      projects, totalWeightKg, pallets, weightPerPallet: pallets ? Math.ceil(totalWeightKg / pallets) : 0,
      materialCents, estimatedMaterialCents, insuranceValueCents: Math.max(100, estimatedMaterialCents), palletCents, sampleCents, freightCents,
      totalNetCents, vatCents, totalGrossCents, totalCents: totalGrossCents, taxRatePercent: 19,
      hasCustom: projects.some((project) => project.customPrice),
      hasUnpriced: projects.some((project) => project.priceOnRequest),
      checkoutEligible: projects.length > 0 && projects.every((project) => project.checkoutEligible),
    };
  }

  function metrics() {
    let lengthCm = numberValue('lengthCm');
    let widthCm = numberValue('widthCm');
    let requestedArea = numberValue('area');
    if (state.use === 'Fensterbank' || state.use === 'Waschtisch') requestedArea = (lengthCm * widthCm) / 10000;
    if (state.use === 'Tisch' && state.tableShape === 'rectangular') {
      lengthCm = numberValue('tableLengthCm'); widthCm = numberValue('tableWidthCm'); requestedArea = (lengthCm * widthCm) / 10000;
    }
    if (state.use === 'Tisch' && state.tableShape === 'round') {
      const radiusCm = numberValue('tableRadiusCm'); requestedArea = Math.PI * Math.pow(radiusCm / 100, 2); lengthCm = radiusCm * 2; widthCm = radiusCm * 2;
    }
    if (state.use === 'Arbeitsplatte' && state.worktopShape === 'straight') {
      lengthCm = numberValue('worktopLengthCm'); widthCm = numberValue('worktopDepthCm'); requestedArea = (lengthCm * widthCm) / 10000;
    }
    if (state.use === 'Arbeitsplatte' && state.worktopShape === 'l-shape') {
      const a=numberValue('worktopLegACm'), da=numberValue('worktopDepthACm'), b=numberValue('worktopLegBCm'), db=numberValue('worktopDepthBCm'); requestedArea = Math.max(0,(a*da+b*db-da*db)/10000); lengthCm=a; widthCm=b;
    }
    if (state.use === 'Arbeitsplatte' && state.worktopShape === 'u-shape') {
      const back=numberValue('worktopBackCm'), left=numberValue('worktopLeftCm'), right=numberValue('worktopRightCm'), depth=numberValue('worktopUDepthCm'); requestedArea = Math.max(0,(back*depth+left*depth+right*depth-2*depth*depth)/10000); lengthCm=back; widthCm=Math.max(left,right);
    }
    let cutoutArea = 0;
    if (state.use === 'Waschtisch' && state.cutoutShape === 'rectangular') cutoutArea = (numberValue('cutoutLengthCm') * numberValue('cutoutWidthCm')) / 10000;
    if (state.use === 'Waschtisch' && state.cutoutShape === 'round') cutoutArea = Math.PI * Math.pow(numberValue('cutoutRadiusCm') / 100, 2);
    requestedArea = Math.max(0, requestedArea - cutoutArea);
    const waste = isDimensionUse() ? 0 : Math.max(0, numberValue('waste'));
    const rawMaterialArea = requestedArea * (1 + waste / 100);
    const materialArea = Math.ceil(rawMaterialArea * 100 - 1e-9) / 100;
    const thicknessCm = numberValue('thickness') || 1;
    const weightKg = Math.round(materialArea * (thicknessCm / 100) * config.density);
    const pallets = weightKg > 0 ? Math.ceil(weightKg / config.palletCapacity) : 0;
    const weightPerPallet = pallets ? Math.ceil(weightKg / pallets) : 0;
    return { requestedArea, waste, materialArea, thicknessCm, weightKg, pallets, weightPerPallet, lengthCm, widthCm, cutoutArea };
  }

  function configureExecutionStep() {
    const dimensions = isDimensionUse();
    const directPurchase = state.purchaseMode === 'direct' && isDirectCheckoutUse();
    if (directPurchase) {
      state.surface = 'Poliert';
      form.elements.thickness.value = '1';
    }
    root.querySelector('.nt-advisor__fields--area').hidden = dimensions;
    root.querySelector('.nt-advisor__fields--dimensions').hidden = !['Fensterbank','Waschtisch'].includes(state.use);
    root.querySelector('[data-worktop]').hidden = state.use !== 'Arbeitsplatte';
    root.querySelector('[data-table-shape]').hidden = state.use !== 'Tisch';
    root.querySelector('[data-cutout]').hidden = state.use !== 'Waschtisch';
    root.querySelector('[data-execution-heading]').textContent = dimensions ? `Welche Maße soll ${state.use === 'Fensterbank' ? 'die Fensterbank' : state.use === 'Arbeitsplatte' ? 'die Arbeitsplatte' : state.use === 'Waschtisch' ? 'der Waschtisch' : 'der Tisch'} haben?` : directPurchase ? 'Welche Menge benötigen Sie?' : 'Welche Menge und Ausführung benötigen Sie?';
    root.querySelector('[data-execution-copy]').textContent = dimensions
      ? 'Bitte geben Sie die fertigen Außenmaße an. Für Maßanfertigungen wird kein Direktpreis angezeigt.'
      : directPurchase
        ? `Für Direktkaufprodukte sind die Oberfläche Poliert und die Stärke 1 cm festgelegt. Die Mindestbestellmenge beträgt ${decimal(directMinimumAreaM2, 0)} m² Bestellfläche inklusive Verschnitt.`
        : 'Für die Transportplanung wird das Gewicht aus Fläche, Stärke und Marmordichte berechnet.';
    root.querySelector('[data-area-label]').textContent = dimensions ? 'Berechnete Nettofläche' : 'Bestellfläche inkl. Verschnitt';
    root.querySelector('.nt-advisor__surface-heading').hidden = directPurchase;
    root.querySelector('[data-field="surface"]').hidden = directPurchase;
    root.querySelector('.nt-advisor__fields--execution').hidden = directPurchase;
    root.querySelectorAll('[data-field="surface"] button').forEach((button) => {
      button.classList.toggle('is-selected', button.dataset.value === state.surface);
    });
    updateShapePanels();
    updateMetrics();
  }

  function updateShapePanels() {
    root.querySelector('.nt-advisor__table-rect').hidden = state.tableShape !== 'rectangular';
    root.querySelector('.nt-advisor__table-round').hidden = state.tableShape !== 'round';
    root.querySelector('.nt-advisor__worktop-straight').hidden = state.worktopShape !== 'straight';
    root.querySelector('.nt-advisor__worktop-l').hidden = state.worktopShape !== 'l-shape';
    root.querySelector('.nt-advisor__worktop-u').hidden = state.worktopShape !== 'u-shape';
    root.querySelector('.nt-advisor__cutout-rect').hidden = state.cutoutShape !== 'rectangular';
    root.querySelector('.nt-advisor__cutout-round').hidden = state.cutoutShape !== 'round';
  }

  function updateMetrics() {
    const current = metrics();
    root.querySelector('[data-output="material-area"]').textContent = current.materialArea ? `${decimal(current.materialArea)} m²` : '–';
    root.querySelector('[data-output="weight"]').textContent = current.weightKg ? `${decimal(current.weightKg, 0)} kg` : '–';
    root.querySelector('[data-output="pallets"]').textContent = current.pallets ? String(current.pallets) : '–';
    invalidateTransportQuote();
  }

  function dimensionDescription() {
    const current = metrics();
    if (state.use === 'Fensterbank') return `${decimal(current.lengthCm)} × ${decimal(current.widthCm)} cm`;
    if (state.use === 'Waschtisch') return `${decimal(current.lengthCm)} × ${decimal(current.widthCm)} cm · Ausschnitt ${state.cutoutShape === 'round' ? `rund, Radius ${decimal(numberValue('cutoutRadiusCm'))} cm` : `rechteckig, ${decimal(numberValue('cutoutLengthCm'))} × ${decimal(numberValue('cutoutWidthCm'))} cm`}`;
    if (state.use === 'Tisch' && state.tableShape === 'round') return `Rund, Radius ${decimal(numberValue('tableRadiusCm'))} cm`;
    if (state.use === 'Tisch') return `Rechteckig, ${decimal(numberValue('tableLengthCm'))} × ${decimal(numberValue('tableWidthCm'))} cm`;
    if (state.use === 'Arbeitsplatte' && state.worktopShape === 'straight') return `Gerade, ${decimal(numberValue('worktopLengthCm'))} × ${decimal(numberValue('worktopDepthCm'))} cm`;
    if (state.use === 'Arbeitsplatte' && state.worktopShape === 'l-shape') return `L-Form, A ${decimal(numberValue('worktopLegACm'))} × ${decimal(numberValue('worktopDepthACm'))} cm, B ${decimal(numberValue('worktopLegBCm'))} × ${decimal(numberValue('worktopDepthBCm'))} cm`;
    if (state.use === 'Arbeitsplatte' && state.worktopShape === 'u-shape') return `U-Form, Rückseite ${decimal(numberValue('worktopBackCm'))} cm, links ${decimal(numberValue('worktopLeftCm'))} cm, rechts ${decimal(numberValue('worktopRightCm'))} cm, Tiefe ${decimal(numberValue('worktopUDepthCm'))} cm`;
    return `${decimal(current.materialArea)} m²`;
  }

  function imageUrl(product) {
    const image = product.featured_image || product.images?.[0];
    if (!image) return '';
    const source = typeof image === 'string' ? image : image.src;
    if (!source) return '';
    return source.startsWith('//') ? `https:${source}` : source;
  }

  function variantsForSurface(product) {
    let variants = product.variants || [];
    if (state.surface) {
      variants = variants.filter((variant) => String(variant.title).toLowerCase().includes(state.surface.toLowerCase()));
    }
    return variants;
  }

  function matchingVariants(product) {
    return variantsForSurface(product).filter((variant) => variant.available);
  }

  function currentVariant(productId) {
    const product = data.products.find((item) => String(item.id) === String(productId));
    if (!product) return null;
    const selectedVariant = state.selected.get(String(productId));
    const availableVariants = matchingVariants(product);
    return availableVariants.find((variant) => String(variant.id) === String(selectedVariant)) || availableVariants[0] || null;
  }

  function configurePurchaseMode() {
    const directAllowed = isDirectCheckoutUse();
    if (!directAllowed) state.purchaseMode = 'request';
    const directButton = root.querySelector('[data-field="purchaseMode"] [data-value="direct"]');
    const requestButton = root.querySelector('[data-field="purchaseMode"] [data-value="request"]');
    directButton.disabled = !directAllowed;
    directButton.classList.toggle('is-selected', state.purchaseMode === 'direct');
    directButton.setAttribute('aria-pressed', String(state.purchaseMode === 'direct'));
    requestButton.classList.toggle('is-selected', state.purchaseMode === 'request');
    requestButton.setAttribute('aria-pressed', String(state.purchaseMode === 'request'));
    root.querySelector('[data-purchase-mode-note]').textContent = directAllowed
      ? state.purchaseMode === 'direct'
        ? `Ab ${decimal(directMinimumAreaM2, 0)} m² inkl. Verschnitt · Poliert · 1 cm · zzgl. Verpackung und Transport nach Deutschland.`
        : state.purchaseMode === 'request'
          ? 'Diese Marmorsorten werden individuell angeboten. Menge und Oberfläche wählen Sie im nächsten Schritt.'
          : 'Wählen Sie zuerst, ob Sie direkt verfügbare oder ausschließlich anfragbare Produkte sehen möchten.'
      : 'Maßanfertigungen sind ausschließlich auf Anfrage erhältlich; Direktkauf ist für diese Kategorie nicht verfügbar.';
    root.querySelector('[data-products-copy]').textContent = state.purchaseMode === 'direct'
      ? 'Bodenfliesen mit Preis auswählen – direkt bestellen oder unverbindlich anfragen.'
      : 'Weitere Marmorsorten für Ihre Bodenfliesen – Preis auf Anfrage.';
  }

  function renderProducts() {
    configurePurchaseMode();
    if (!state.purchaseMode) {
      productContainer.innerHTML = '';
      root.querySelector('[data-products-count]').hidden = true;
      root.querySelector('[data-products-empty]').hidden = true;
      return;
    }
    const color = value('color').toLowerCase();
    const search = value('product-search').toLowerCase();
    const products = (data.products || []).filter((product) => {
      const text = productText(product);
      const availabilityMatches = state.purchaseMode === 'direct' ? isDirectPurchaseProduct(product) : !isDirectPurchaseProduct(product);
      return availabilityMatches && (!color || text.includes(color)) && (!search || text.includes(search));
    });

    productContainer.innerHTML = products.map((product) => {
      const selected = state.selected.has(String(product.id));
      const variant = currentVariant(product.id);
      const surfaceVariants = variantsForSurface(product);
      const availableVariants = matchingVariants(product);
      const showPrice = state.purchaseMode === 'direct' && isDirectPurchaseProduct(product);
      const options = availableVariants.map((item) => `<option value="${item.id}" ${variant && String(item.id) === String(variant.id) ? 'selected' : ''}>${escapeHtml(item.title)}${showPrice ? ` · ${currency(grossFromNet(item.price))}/m² inkl. MwSt.` : ''}</option>`).join('');
      const priceLabel = !availableVariants.length
        ? surfaceVariants.length
          ? 'Diese Ausführung ist derzeit nicht verfügbar'
          : 'Für diese Oberfläche derzeit nicht verfügbar'
        : showPrice
        ? `ab ${currency(grossFromNet(Math.min(...availableVariants.map((item) => Number(item.price)))))} / m² inkl. MwSt.`
        : 'Auf Anfrage erhältlich';
      return `<article class="nt-advisor__product${selected ? ' is-selected' : ''}" data-product-id="${product.id}">
        ${imageUrl(product) ? `<img src="${escapeHtml(imageUrl(product))}" alt="${escapeHtml(product.title)}" loading="lazy">` : '<div class="nt-advisor__product-image-placeholder"></div>'}
        <div class="nt-advisor__product-body">
          <h3>${escapeHtml(product.title)}</h3>
          <span class="nt-advisor__product-price">${priceLabel}</span>
          <select aria-label="Variante für ${escapeHtml(product.title)}" ${availableVariants.length ? '' : 'disabled'}>${options || '<option>Nicht verfügbar</option>'}</select>
          <button type="button" ${availableVariants.length ? '' : 'disabled'}>${selected ? 'Weiter mit Auswahl' : 'Auswählen & weiter'}</button>
        </div>
      </article>`;
    }).join('');
    const count = root.querySelector('[data-products-count]');
    count.hidden = false;
    count.textContent = `${products.length} ${products.length === 1 ? 'Marmorsorte' : 'Marmorsorten'} · Auswählen und Fläche angeben`;
    root.querySelector('[data-products-empty]').hidden = products.length > 0;
  }

  function selectedItems() {
    return [...state.selected.keys()].map((productId) => {
      const product = data.products.find((item) => String(item.id) === productId);
      return { product, variant: currentVariant(productId) };
    }).filter((item) => item.product && item.variant);
  }

  function renderSummary() {
    const costs = pricing();
    const quote = activeTransportQuote();
    const projectCards = costs.projects.map((project, index) => {
      const products = project.items.map(({product, variant}) => `<div class="nt-advisor__summary-row"><span>${escapeHtml(product.title)}<small> · ${escapeHtml(variant.title)}</small></span><b>${project.priceOnRequest ? 'Preis auf Anfrage' : `${currency(grossFromNet(Number(variant.price) * project.billingUnits))} brutto`}</b></div>`).join('');
      const measurement = project.customPrice ? project.measurement : `${decimal(project.metrics.materialArea)} m² / ${project.billingUnits} Abrechnungseinheit(en)`;
      return `<div class="nt-advisor__summary-card"><h3>Position ${index + 1}: ${escapeHtml(project.use)}${project.priceOnRequest ? ' · Auf Anfrage' : ' · Direktkauf'}</h3>${products}
        <div class="nt-advisor__summary-row"><span>${project.customPrice ? 'Fertigmaße' : 'Bestellfläche'}</span><b>${escapeHtml(measurement)}</b></div>
        <div class="nt-advisor__summary-row"><span>Oberfläche · Stärke</span><b>${escapeHtml(project.surface)} · ${decimal(project.metrics.thicknessCm)} cm</b></div>
        <div class="nt-advisor__summary-row"><span>Gewicht</span><b>${decimal(project.metrics.weightKg, 0)} kg</b></div>
      </div>`;
    }).join('');
    const hasPostalCode = /^\d{5}$/.test(value('postalCode'));
    const transport = quote ? currency(quote.priceCents) : 'Noch nicht ermittelt';
    root.querySelector('[data-summary-projects]').innerHTML = projectCards;
    root.querySelector('[data-summary-packaging]').innerHTML = `
      <div class="nt-advisor__summary-card"><h3>Verpackung und Zusatzwünsche</h3>
        <div class="nt-advisor__summary-row"><span>${costs.pallets} Einwegpalette(n), 120 × 80 × 100 cm</span><b>${data.palletVariant ? `${currency(grossFromNet(costs.palletCents))} brutto` : 'Produkt fehlt'}</b></div>
        <div class="nt-advisor__summary-row"><span>Musterstück inkl. Versand</span><b>${checked('sample') ? (data.sampleVariant ? `${currency(grossFromNet(costs.sampleCents))} brutto` : 'Produkt fehlt') : 'Nein'}</b></div>
        <div class="nt-advisor__summary-row"><span>Fliesenleger angefragt</span><b>${checked('installer') ? 'Ja' : 'Nein'}</b></div>
      </div>`;
    root.querySelector('[data-summary-transport]').innerHTML = `
      <div class="nt-advisor__summary-card"><h3>Transport</h3>
        <div class="nt-advisor__summary-row"><span>${hasPostalCode ? `Lieferung nach PLZ ${escapeHtml(value('postalCode'))}, Deutschland` : 'Deutsche Lieferpostleitzahl'}</span><b>${transport}</b></div>
        <div class="nt-advisor__summary-row"><span>Leistungen</span><b>Versichert · Telefonische Lieferungsankündigung 30 Minuten vor Warenzustellung</b></div>
      </div>`;
    root.querySelector('[data-summary-total]').innerHTML = `
      <div class="nt-advisor__summary-card nt-advisor__summary-total">
        <div class="nt-advisor__summary-row"><span>Nettosumme</span><b>${costs.hasUnpriced ? 'teilweise auf Anfrage' : currency(costs.totalNetCents)}</b></div>
        <div class="nt-advisor__summary-row"><span>Umsatzsteuer 19 %</span><b>${costs.hasUnpriced ? 'nach finaler Kalkulation' : currency(costs.vatCents)}</b></div>
        <div class="nt-advisor__summary-row"><span>Voraussichtliche Gesamtsumme brutto</span><b>${costs.hasUnpriced ? 'Mindestens eine Position auf Anfrage' : quote ? currency(costs.totalGrossCents) : `${currency(costs.totalGrossCents)} zzgl. Transport`}</b></div>
      </div>`;
  }

  function renderContactTotals() {
    const container = root.querySelector('[data-contact-total]');
    if (!container) return;
    const quote = activeTransportQuote();
    if (!quote) {
      container.hidden = true;
      container.innerHTML = '';
      return;
    }
    const costs = pricing();
    container.hidden = false;
    container.innerHTML = `<div class="nt-advisor__summary-card nt-advisor__summary-total">
      <div class="nt-advisor__summary-row"><span>Transport brutto</span><b>${currency(quote.priceCents)}</b></div>
      <div class="nt-advisor__summary-row"><span>Voraussichtliche Gesamtsumme brutto</span><b>${costs.hasUnpriced ? 'Mindestens eine Position auf Anfrage' : currency(costs.totalGrossCents)}</b></div>
    </div>`;
  }

  function showError(message) {
    errorBox.textContent = message;
    errorBox.hidden = false;
    errorBox.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  function clearError() { errorBox.hidden = true; errorBox.textContent = ''; }

  function invalidateTransportQuote({render = true} = {}) {
    state.transportQuote = null;
    state.transportQuoteSignature = '';
    updateTransportStatus('idle');
    renderContactTotals();
    if (render && state.step === 4) renderSummary();
  }

  function validateTransportPostalCode() {
    clearError();
    if (!/^\d{5}$/.test(value('postalCode'))) {
      showError('Bitte geben Sie eine gültige fünfstellige deutsche Postleitzahl ein.');
      return false;
    }
    if ((value('countryCode') || 'DE') !== 'DE') {
      showError('Die direkte Transportpreisermittlung in Schritt 3 ist derzeit für Lieferadressen in Deutschland verfügbar.');
      return false;
    }
    return true;
  }

  function validateDeliveryDetails({requirePrivacy = false} = {}) {
    clearError();
    const required = ['firstName','lastName','address1','postalCode','city','countryCode','email'];
    if (required.some((name) => !value(name))) return showError('Bitte füllen Sie die vollständigen Liefer- und Kontaktdaten aus.'), false;
    if (!form.elements.email.checkValidity()) return showError('Bitte geben Sie eine gültige E-Mail-Adresse ein.'), false;
    if (value('phone') && value('phone').replace(/\D/g, '').length < 7) return showError('Bitte geben Sie eine gültige Telefonnummer ein oder lassen Sie das optionale Feld leer.'), false;
    if (requirePrivacy && !checked('privacy')) return showError('Bitte bestätigen Sie die Datenschutzerklärung.'), false;
    return true;
  }

  function validateStep(step) {
    clearError();
    if (step === 2 && !state.purchaseMode) return showError('Bitte wählen Sie zuerst „Direktkauf“ oder „Auf Anfrage erhältlich“ aus.'), false;
    if (step === 2 && selectedItems().length === 0) return showError('Bitte wählen Sie mindestens ein Marmorprodukt aus.'), false;
    if (step === 3) {
      if (['Fensterbank','Waschtisch'].includes(state.use) && (numberValue('lengthCm') <= 0 || numberValue('widthCm') <= 0)) return showError('Bitte geben Sie Länge und Breite der Maßanfertigung ein.'), false;
      if (state.use === 'Tisch' && !state.tableShape) return showError('Bitte wählen Sie eine Tischform aus.'), false;
      if (state.use === 'Tisch' && state.tableShape === 'rectangular' && (numberValue('tableLengthCm') <= 0 || numberValue('tableWidthCm') <= 0)) return showError('Bitte geben Sie Länge und Breite des rechteckigen Tischs ein.'), false;
      if (state.use === 'Tisch' && state.tableShape === 'round' && numberValue('tableRadiusCm') <= 0) return showError('Bitte geben Sie den Radius des runden Tischs ein.'), false;
      if (state.use === 'Arbeitsplatte' && !state.worktopShape) return showError('Bitte wählen Sie eine Form der Arbeitsplatte aus.'), false;
      if (state.use === 'Arbeitsplatte' && state.worktopShape === 'straight' && (numberValue('worktopLengthCm') <= 0 || numberValue('worktopDepthCm') <= 0)) return showError('Bitte geben Sie Länge und Tiefe der geraden Arbeitsplatte ein.'), false;
      if (state.use === 'Arbeitsplatte' && state.worktopShape === 'l-shape' && ['worktopLegACm','worktopDepthACm','worktopLegBCm','worktopDepthBCm'].some((name) => numberValue(name) <= 0)) return showError('Bitte geben Sie alle vier Maße der L-Arbeitsplatte ein.'), false;
      if (state.use === 'Arbeitsplatte' && state.worktopShape === 'u-shape' && ['worktopBackCm','worktopLeftCm','worktopRightCm','worktopUDepthCm'].some((name) => numberValue(name) <= 0)) return showError('Bitte geben Sie alle vier Maße der U-Arbeitsplatte ein.'), false;
      if (!isDimensionUse() && numberValue('area') <= 0) return showError('Bitte geben Sie eine Fläche größer als 0 m² ein.'), false;
      const currentMetrics = metrics();
      if (state.purchaseMode === 'direct' && isDirectCheckoutUse() && currentMetrics.materialArea < directMinimumAreaM2) return showError(`Für direkt bestellbare Boden- und Wandfliesen beträgt die Mindestbestellmenge ${decimal(directMinimumAreaM2, 0)} m² inklusive Verschnitt. Ihre aktuelle Bestellfläche beträgt ${decimal(currentMetrics.materialArea)} m².`), false;
      if (!state.surface) return showError('Bitte wählen Sie eine Oberflächenbearbeitung aus.'), false;
      if (state.use === 'Waschtisch' && !state.cutoutShape) return showError('Bitte wählen Sie die Form des Waschbeckenausschnitts.'), false;
      if (state.use === 'Waschtisch' && state.cutoutShape === 'rectangular' && (numberValue('cutoutLengthCm') <= 0 || numberValue('cutoutWidthCm') <= 0)) return showError('Bitte geben Sie Länge und Breite des rechteckigen Ausschnitts ein.'), false;
      if (state.use === 'Waschtisch' && state.cutoutShape === 'round' && numberValue('cutoutRadiusCm') <= 0) return showError('Bitte geben Sie den Radius des runden Ausschnitts ein.'), false;
      if (currentMetrics.materialArea <= 0) return showError('Der Ausschnitt muss kleiner als die gesamte Waschtischfläche sein.'), false;
    }
    if (step === 5) return validateDeliveryDetails({requirePrivacy: true});
    return true;
  }

  function updateAddedProductsStatus() {
    const status = root.querySelector('[data-added-products-status]');
    status.hidden = state.projects.length === 0;
    status.textContent = state.projects.length === 1 ? '1 weitere Position wurde vorgemerkt.' : `${state.projects.length} weitere Positionen wurden vorgemerkt.`;
  }

  function resetCurrentProject() {
    state.use = 'Boden'; state.surface = 'Poliert'; state.cutoutShape = ''; state.tableShape = ''; state.worktopShape = ''; state.purchaseMode = 'direct';
    state.selected.clear();
    invalidateTransportQuote({render: false});
    const resetNames = ['area','lengthCm','widthCm','cutoutLengthCm','cutoutWidthCm','cutoutRadiusCm','tableLengthCm','tableWidthCm','tableRadiusCm','worktopLengthCm','worktopDepthCm','worktopLegACm','worktopDepthACm','worktopLegBCm','worktopDepthBCm','worktopBackCm','worktopLeftCm','worktopRightCm','worktopUDepthCm'];
    resetNames.forEach((name) => { if (form.elements[name]) form.elements[name].value = ''; });
    if (form.elements.waste) form.elements.waste.value = root.dataset.wastePercent || '10';
    if (form.elements.thickness) form.elements.thickness.value = '1';
    if (form.elements.color) form.elements.color.value = '';
    if (form.elements['product-search']) form.elements['product-search'].value = '';
    root.querySelectorAll('[data-field] button').forEach((button) => button.classList.remove('is-selected'));
  }

  function addMoreProduct() {
    if (!validateStep(2) || !validateStep(3)) return;
    const project = currentProject();
    if (!project) return showError('Die aktuelle Produktauswahl konnte nicht gespeichert werden.');
    state.projects.push(project);
    resetCurrentProject();
    setStep(firstStep);
  }

  function setStep(step, {focus = true} = {}) {
    state.step = Math.min(5, Math.max(firstStep, step));
    const displayStep = state.step - firstStep + 1;
    const stepEventName = `mb_step_${state.step}_view`;
    if (!trackedStepViews.has(stepEventName)) {
      trackedStepViews.add(stepEventName);
      trackAdvisorEvent(stepEventName);
    }
    root.querySelectorAll('.nt-advisor__step').forEach((element) => element.classList.toggle('is-active', Number(element.dataset.step) === state.step));
    root.querySelector('.nt-advisor__progress i').style.width = `${displayStep / totalSteps * 100}%`;
    root.querySelector('.nt-advisor__progress em').textContent = displayStep;
    root.querySelector('[data-action="back"]').hidden = state.step === firstStep;
    root.querySelector('[data-final-actions]').hidden = state.step !== 5;
    const checkoutButton = root.querySelector('[data-action="checkout"]');
    const checkoutAvailable = config.checkoutEnabled && pricing().checkoutEligible && value('countryCode') === 'DE';
    checkoutButton.disabled = !checkoutAvailable;
    checkoutButton.title = checkoutAvailable
      ? 'Sichere Shopify-Zahlungsseite mit aktuellem Cargoboard-Preis erstellen'
      : !config.checkoutEnabled
        ? 'Direkte Zahlung ist in den Einstellungen dieses Theme-Entwurfs deaktiviert'
        : value('countryCode') !== 'DE'
          ? 'Direkte Zahlung ist derzeit ausschließlich für Lieferadressen in Deutschland verfügbar'
          : 'Direkte Zahlung ist nur für mit „Direktkauf“ markierte Boden- oder Wandprodukte möglich';
    if (state.step === 2) renderProducts();
    if (state.step === 3) configureExecutionStep();
    if (state.step === 4) {
      updateAddedProductsStatus();
      renderSummary();
      const quote = activeTransportQuote();
      updateTransportStatus(quote ? 'success' : 'idle', quote ? `${currency(quote.priceCents)}${quote.provisional ? ' · auf Basis des hinterlegten Versicherungswerts' : ''}` : '', Boolean(quote?.provisional));
    }
    if (state.step === 5) {
      renderContactTotals();
      const postalCodeOutput = root.querySelector('[data-contact-postal-code]');
      if (postalCodeOutput) postalCodeOutput.textContent = value('postalCode') || '–';
    }
    clearError();
    const activeLegend = root.querySelector('.nt-advisor__step.is-active legend');
    activeLegend.setAttribute('tabindex', '-1');
    if (focus) activeLegend.focus({preventScroll: true});
    window.requestAnimationFrame(() => {
      const workspace = root.querySelector('.nt-advisor__workspace');
      const headerHeight = Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
      const targetTop = window.scrollY + workspace.getBoundingClientRect().top - headerHeight;
      window.scrollTo({top: Math.max(0, targetTop), behavior: 'smooth'});
    });
  }

  function updateTransportStatus(status, message = '', provisional = false) {
    const transportStatus = root.querySelector('[data-transport-status]');
    if (!transportStatus) return;
    transportStatus.classList.remove('is-success', 'is-error');
    if (status === 'success') {
      transportStatus.classList.add('is-success');
      transportStatus.innerHTML = `<b>${provisional ? 'Vorläufiger Transportpreis ermittelt' : 'Transportpreis ermittelt'}</b><span>${escapeHtml(message)}</span>`;
    } else if (status === 'error') {
      transportStatus.classList.add('is-error');
      transportStatus.innerHTML = `<b>Transportpreis nicht verfügbar</b><span>${escapeHtml(message)}</span>`;
    } else if (status === 'loading') {
      transportStatus.innerHTML = '<b>Transportpreis wird ermittelt …</b><span>Versicherung und telefonische Lieferungsankündigung sind eingeschlossen. Es wird kein Transport gebucht.</span>';
    } else {
      transportStatus.innerHTML = '<b>Transportpreis noch nicht ermittelt</b><span>Geben Sie die Lieferpostleitzahl ein. Die Preisabfrage ist unverbindlich und bucht keinen Transport.</span>';
    }
  }

  function transportPayload() {
    const costs = pricing();
    const materialAreaM2 = costs.projects.reduce((sum, project) => sum + project.metrics.materialArea, 0);
    return {
      product: 'STANDARD',
      shipper: { postCode: config.origin.postalCode, city: config.origin.city, countryCode: config.origin.countryCode },
      previewOnly: true,
      consignee: { postCode: value('postalCode'), countryCode: 'DE' },
      lines: [{
        content: 'Marmor auf Einwegpalette', quantity: costs.pallets, packageType: 'EP',
        lengthCm: config.pallet.length, widthCm: config.pallet.width, heightCm: config.pallet.height,
        weightPerUnitKg: costs.weightPerPallet, totalWeightKg: costs.totalWeightKg,
      }],
      services: { insurance: true, phoneAvis: true },
      valueOfGoods: { amountCents: costs.insuranceValueCents, currency: data.currency || 'EUR', estimated: costs.hasUnpriced },
      metadata: { source: 'shopify-marmorberater', materialAreaM2, projectCount: costs.projects.length, insuranceValueEstimated: costs.hasUnpriced },
    };
  }

  async function requestTransportQuote({force = false} = {}) {
    if (state.transportLoading) return state.transportPromise;
    if (activeTransportQuote() && !force) return state.transportQuote;
    if (!validateTransportPostalCode()) {
      trackAdvisorEvent('mb_validation_error', { mb_validation_step: 4, mb_validation_context: 'transport_postal_code' });
      return;
    }
    if (!config.liveQuote) {
      updateTransportStatus('error', 'Die Live-Abfrage ist im Theme-Editor noch deaktiviert. Der Server-Endpunkt kann später ohne Änderung der Beraterlogik angeschlossen werden.');
      return;
    }
    const button = root.querySelector('[data-action="quote-transport"]');
    state.transportLoading = true;
    updateTransportStatus('loading');
    button.disabled = true; button.textContent = 'Transportpreis wird ermittelt …';
    state.transportPromise = (async () => { try {
      const quoteUsesEstimatedValue = pricing().hasUnpriced;
      const response = await fetch(`${config.backendUrl}${config.quoteEndpoint}`, {
        method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify(transportPayload()),
      });
      if (!response.ok) {
        const failure = await response.json().catch(() => ({}));
        throw new Error(failure.detail || `Serverantwort ${response.status}`);
      }
      const result = await response.json();
      const priceCents = Number(result.price_cents ?? result.priceCents ?? (Number(result.price ?? result.amount) * 100));
      if (!Number.isFinite(priceCents) || priceCents <= 0) throw new Error('Kein gültiger Transportpreis empfangen');
      const netPriceCents = Number(result.netPriceCents ?? result.net_price_cents ?? Math.round(priceCents / (1 + taxRate)));
      if (!Number.isFinite(netPriceCents) || netPriceCents <= 0) throw new Error('Kein gültiger Netto-Transportpreis empfangen');
      state.transportQuote = {
        priceCents: Math.round(priceCents), netPriceCents: Math.round(netPriceCents), reference: result.reference ?? result.quotation_id ?? result.id,
        pickupOn: result.pickupOn ?? result.pickup_on,
        validUntil: result.valid_until ?? result.validUntil, checkoutReady: result.checkout_ready === true || result.checkoutReady === true,
        provisional: quoteUsesEstimatedValue,
      };
      state.transportQuoteSignature = transportQuoteSignature();
      updateTransportStatus('success', `${currency(state.transportQuote.priceCents)}${quoteUsesEstimatedValue ? ' · auf Basis des hinterlegten Versicherungswerts' : ''}`, quoteUsesEstimatedValue);
      trackAdvisorEvent('mb_transport_quote_success', { mb_transport_quote_provisional: quoteUsesEstimatedValue ? 'yes' : 'no' });
      renderContactTotals();
      if (state.step === 4) renderSummary();
    } catch (error) {
      trackAdvisorEvent('mb_transport_quote_error');
      state.transportQuote = null;
      state.transportQuoteSignature = '';
      updateTransportStatus('error', `Bitte versuchen Sie es erneut oder fordern Sie ein unverbindliches Angebot an. (${error.message})`);
      renderContactTotals();
      if (state.step === 4) renderSummary();
    } finally {
      state.transportLoading = false;
      button.disabled = false; button.textContent = activeTransportQuote() ? 'Transportpreis neu ermitteln' : 'Transportpreis ermitteln';
    } })();
    try { return await state.transportPromise; }
    finally { state.transportPromise = null; }
  }

  function cartItems() {
    const costs = pricing();
    const items = costs.projects.flatMap((project, projectIndex) => {
      const common = {
        'Berater-Position': String(projectIndex + 1), 'Einsatzbereich': project.use,
        'Quadratmeter benötigt': decimal(project.metrics.materialArea), 'Kilogramm benötigt': String(project.metrics.weightKg),
        'Stärke': `${project.metrics.thicknessCm} cm`, 'Oberfläche': project.surface || 'nicht festgelegt',
        '_Marmorberater': 'Ja', '_Berater-Version': 'Marmorberater 8.7.3',
      };
      if (project.customPrice) {
        common['Fertigmaß / Form'] = project.measurement;
      }
      if (project.priceOnRequest) {
        common['Preisstatus'] = 'Preis auf Anfrage';
      }
      return project.items.map(({product, variant}) => ({
        id: Number(variant.id), quantity: project.billingUnits,
        properties: {...common, 'Marmorsorte': product.title},
      }));
    });
    if (costs.pallets && data.palletVariant) items.push({ id: Number(data.palletVariant.id), quantity: costs.pallets, properties: {'_Marmorberater': 'Ja', 'Verpackung für': 'Marmorberater', 'Maße': '120 × 80 × 100 cm'} });
    if (checked('sample') && data.sampleVariant) items.push({ id: Number(data.sampleVariant.id), quantity: 1, properties: {'_Marmorberater': 'Ja', 'Muster für': costs.projects.flatMap((project) => project.items.map((item) => item.product.title)).join(', '), 'Versand': 'Im Musterpreis enthalten'} });
    return items;
  }

  function isAdvisorCartItem(item) {
    const properties = item?.properties || {};
    return properties._Marmorberater === 'Ja'
      || Boolean(properties['_Berater-Version'])
      || properties['Verpackung für'] === 'Marmorberater'
      || Boolean(properties['Muster für']);
  }

  async function removeExistingAdvisorCartItems() {
    const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {headers: {'Accept':'application/json'}});
    if (!cartResponse.ok) throw new Error('Der vorhandene Warenkorb konnte nicht geprüft werden.');
    const cart = await cartResponse.json();
    const updates = {};
    (cart.items || []).filter(isAdvisorCartItem).forEach((item) => { updates[item.key] = 0; });
    if (!Object.keys(updates).length) return;
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
      method: 'POST', headers: {'Content-Type':'application/json', 'Accept':'application/json'}, body: JSON.stringify({updates}),
    });
    if (!response.ok) throw new Error('Eine vorherige Marmorberater-Zusammenstellung konnte nicht ersetzt werden.');
  }

  async function writeCart() {
    const items = cartItems();
    if (!items.length) throw new Error('Keine verfügbaren Shopify-Varianten ausgewählt.');
    await removeExistingAdvisorCartItems();
    const addResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST', headers: {'Content-Type':'application/json', 'Accept':'application/json'}, body: JSON.stringify({items}),
    });
    if (!addResponse.ok) throw new Error((await addResponse.json()).description || 'Warenkorb konnte nicht befüllt werden.');
    const attributes = {
      'Berater': 'Marmorberater', 'Fliesenleger gewünscht': checked('installer') ? 'Ja' : 'Nein',
      'Vorname': value('firstName'), 'Nachname': value('lastName'), 'Unternehmen': value('company'),
      'Straße': value('address1'), 'Postleitzahl': value('postalCode'), 'Ort': value('city'), 'Land': value('countryCode'),
      'E-Mail': value('email'), 'Telefon': value('phone'),
      'Lieferadresse': `${value('address1')}, ${value('postalCode')} ${value('city')}, ${value('countryCode')}`,
      'Cargoboard Referenz': activeTransportQuote()?.reference || 'noch nicht ermittelt',
      'Cargoboard Preis': activeTransportQuote() ? currency(activeTransportQuote().priceCents) : 'noch nicht ermittelt',
      'Transportversicherung': 'Ja', 'Telefonische Avisierung': 'Ja',
    };
    const customerNote = `Marmorberater – ${value('firstName')} ${value('lastName')}\n${value('company') ? `${value('company')}\n` : ''}${value('address1')}\n${value('postalCode')} ${value('city')}, ${value('countryCode')}\n${value('email')} · ${value('phone')}`;
    const updateResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
      method: 'POST', headers: {'Content-Type':'application/json', 'Accept':'application/json'}, body: JSON.stringify({attributes, note: customerNote}),
    });
    if (!updateResponse.ok) throw new Error('Projektangaben konnten nicht am Warenkorb gespeichert werden.');
  }

  async function goToCart({fallback = false} = {}) {
    if (!validateStep(5)) return false;
    if (!data.palletVariant) return showError('Das Palettenprodukt ist in der Section noch nicht ausgewählt oder nicht verfügbar.'), false;
    if (checked('sample') && !data.sampleVariant) return showError('Das Musterprodukt ist in der Section noch nicht ausgewählt oder nicht verfügbar.'), false;
    clearError();
    try {
      await writeCart();
      if (fallback) {
        sessionStorage.setItem('nt_mb_request_fallback', state.sessionId);
        trackAdvisorEvent('mb_request_fallback_cart');
      }
      window.location.assign(fallback ? '/cart?mb_fallback=1' : '/cart');
      return true;
    } catch (error) {
      trackAdvisorEvent('mb_request_fallback_error');
      showError(`Die Anfrage konnte weder direkt noch über den sicheren Ersatzweg vorbereitet werden. Bitte versuchen Sie es erneut. (${error.message})`);
      return false;
    }
  }

  const sleep = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

  async function postAdvisorRequest(payload) {
    let lastError = null;
    for (let attempt = 1; attempt <= 2; attempt += 1) {
      try {
        const response = await fetch(`${config.backendUrl}${config.requestEndpoint}`, {
          method: 'POST',
          headers: {'Content-Type':'application/json', 'Accept':'application/json'},
          body: JSON.stringify(payload),
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) {
          const error = new Error(result.detail || `Angebotsserver antwortet mit Status ${response.status}`);
          error.status = response.status;
          throw error;
        }
        if (result.ok !== true) throw new Error(result.message || 'Der Angebotsserver hat die Anfrage nicht bestätigt.');
        return result;
      } catch (error) {
        lastError = error;
        const retryable = !error.status || Number(error.status) >= 500 || Number(error.status) === 409;
        if (attempt < 2 && retryable) {
          await sleep(800);
          continue;
        }
        break;
      }
    }
    throw lastError || new Error('Die Anfrage konnte nicht übertragen werden.');
  }

  function showRequestSuccess(result = {}) {
    const box = root.querySelector('[data-request-success]');
    box.hidden = false;
    box.innerHTML = '<b>Vielen Dank – Ihre Anfrage wurde erfolgreich übermittelt.</b><span>Wir prüfen Ihre Zusammenstellung und melden uns zeitnah mit dem weiteren Vorgehen.</span>';
    const button = root.querySelector('[data-action="request"]');
    button.disabled = true;
    button.textContent = 'Anfrage gesendet ✓';
    state.requestSubmitted = true;
    box.scrollIntoView({behavior: 'smooth', block: 'nearest'});
  }

  async function submitAdvisorRequest() {
    if (state.requestSubmitted) return;
    if (!validateStep(5)) {
      trackAdvisorEvent('mb_validation_error', { mb_validation_step: 5, mb_validation_context: 'request' });
      return;
    }
    if (!data.palletVariant) return showError('Das Palettenprodukt ist in der Section noch nicht ausgewählt oder nicht verfügbar.');
    if (checked('sample') && !data.sampleVariant) return showError('Das Musterprodukt ist in der Section noch nicht ausgewählt oder nicht verfügbar.');
    const button = root.querySelector('[data-action="request"]');
    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Anfrage wird übermittelt …';
    clearError();
    try {
      if (state.transportLoading) await state.transportPromise;
      const result = await postAdvisorRequest({
        ...backendOfferPayload(),
        sessionId: state.sessionId,
        advisorVersion,
      });
      trackAdvisorEvent('mb_request_success', { mb_request_reused: result.reused ? 'yes' : 'no' });
      showRequestSuccess(result);
    } catch (error) {
      trackAdvisorEvent('mb_request_backend_error');
      button.textContent = 'Ersatzweg wird vorbereitet …';
      const redirected = await goToCart({fallback: true});
      if (!redirected) {
        button.disabled = false;
        button.textContent = originalText;
      }
    }
  }

  function offerLines() {
    const costs = pricing();
    const quote = activeTransportQuote();
    const lines = costs.projects.flatMap((project) => project.items.map(({product, variant}) => ({
      position: 0,
       quantity: project.customPrice ? '1 Maßteil' : `${decimal(project.metrics.materialArea)} m²`,
       description: `${project.use}: ${product.title}, ${variant.title}, ${project.metrics.thicknessCm} cm${project.customPrice ? ` · ${project.measurement}` : ''}`,
       unit: project.priceOnRequest ? 'auf Anfrage' : currency(variant.price),
       total: project.priceOnRequest ? 'auf Anfrage' : currency(Number(variant.price) * project.billingUnits),
    })));
    lines.forEach((line, index) => { line.position = index + 1; });
    lines.push({position: lines.length + 1, quantity: `${costs.pallets} Stk.`, description: 'Einwegpalette 120 × 80 × 100 cm', unit: data.palletVariant ? currency(data.palletVariant.price) : '–', total: currency(costs.palletCents)});
    if (checked('sample')) lines.push({position: lines.length + 1, quantity: '1 Stk.', description: `Musterstück inkl. Versand für ${costs.projects.flatMap((project) => project.items.map((item) => item.product.title)).join(', ')}`, unit: currency(costs.sampleCents), total: currency(costs.sampleCents)});
    lines.push({position: lines.length + 1, quantity: 'Extern', description: `${quote?.provisional ? 'Vorläufig kalkulierter, ' : ''}versicherter Versand inkl. telefonischer Avisierung`, unit: quote ? currency(costs.freightCents) : 'offen', total: quote ? currency(costs.freightCents) : 'offen'});
    return lines;
  }

  function backendOfferPayload() {
    const costs = pricing();
    const quote = activeTransportQuote();
    return {
      type: 'unverbindliches-angebot', source: 'marmorberater', advisorVersion, createdAt: new Date().toISOString(), customer: {
        firstName: value('firstName'), lastName: value('lastName'), company: value('company'),
        address1: value('address1'), postalCode: value('postalCode'), city: value('city'), countryCode: value('countryCode'),
        email: value('email'), phone: value('phone'),
      },
      projects: costs.projects.map((project) => ({
        use: project.use, surface: project.surface, measurement: project.measurement, customPrice: project.customPrice, billingUnits: project.billingUnits,
        requestOnly: project.requestOnly, priceOnRequest: project.priceOnRequest, purchaseMode: project.purchaseMode,
        metrics: project.metrics, items: project.items.map(({product, variant}) => ({productId: product.id, productTitle: product.title, variantId: variant.id, variantTitle: variant.title, priceCents: Number(variant.price)})),
      })),
      extras: {sample: checked('sample'), installer: checked('installer')}, transport: transportPayload(),
      transportQuote: quote ? {...quote, insuranceValueEstimated: Boolean(quote.provisional)} : null,
      totals: {materialCents: costs.materialCents, palletCents: costs.palletCents, sampleCents: costs.sampleCents, freightCents: costs.freightCents, totalNetCents: costs.totalNetCents, vatCents: costs.vatCents, totalGrossCents: costs.totalGrossCents, totalCents: costs.totalGrossCents, taxRatePercent: costs.taxRatePercent, hasCustom: costs.hasCustom, hasUnpriced: costs.hasUnpriced},
    };
  }

  async function createDirectCheckout() {
    if (!validateStep(5)) return;
    const costs = pricing();
    if (!costs.checkoutEligible) return showError('Direkte Zahlung ist nur möglich, wenn alle Positionen Boden oder Wand sind und jedes Produkt den Shopify-Tag „Direktkauf“ trägt.');
    if (costs.projects.some((project) => project.checkoutEligible && project.metrics.materialArea < directMinimumAreaM2)) return showError(`Für jede direkt bestellbare Boden- oder Wandposition gilt eine Mindestbestellmenge von ${decimal(directMinimumAreaM2, 0)} m² inklusive Verschnitt.`);
    if (value('countryCode') !== 'DE') return showError('Direkte Zahlung ist derzeit ausschließlich für Lieferadressen in Deutschland verfügbar. Für andere Länder erstellen wir ein unverbindliches Angebot.');
    if (!config.checkoutEnabled) return showError('Die direkte Zahlung für Boden und Wand ist in den Section-Einstellungen deaktiviert.');
    if (!data.palletVariant) return showError('Das Palettenprodukt ist in der Section noch nicht ausgewählt oder nicht verfügbar.');
    if (checked('sample') && !data.sampleVariant) return showError('Das Musterprodukt ist in der Section noch nicht ausgewählt oder nicht verfügbar.');
    const button = root.querySelector('[data-action="checkout"]');
    const checkoutWindow = window.Shopify?.designMode ? window.open('about:blank', '_blank') : null;
    if (checkoutWindow) {
      checkoutWindow.document.title = 'Shopify-Zahlungsseite wird vorbereitet';
      checkoutWindow.document.body.innerHTML = '<p style="font:16px system-ui;padding:32px">Die sichere Shopify-Zahlungsseite mit aktuellem Transportpreis wird erstellt …</p>';
    }
    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Sichere Zahlungsseite wird erstellt …';
    clearError();
    try {
      const payload = {
        ...backendOfferPayload(),
        sessionId: state.sessionId,
        palletVariantId: data.palletVariant.id,
        sampleVariantId: checked('sample') ? data.sampleVariant?.id : null,
      };
      const response = await fetch(`${config.backendUrl}${config.directCheckoutEndpoint}`, {
        method: 'POST', headers: {'Content-Type':'application/json', 'Accept':'application/json'}, body: JSON.stringify(payload),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.detail || `Zahlungsserver antwortet mit Status ${response.status}`);
      if (!result.invoiceUrl || !/^https:\/\//i.test(result.invoiceUrl)) throw new Error('Shopify hat keine sichere Zahlungsadresse geliefert.');
      trackAdvisorEvent('mb_checkout_ready');
      if (checkoutWindow && !checkoutWindow.closed) checkoutWindow.location.assign(result.invoiceUrl);
      else window.location.assign(result.invoiceUrl);
    } catch (error) {
      trackAdvisorEvent('mb_checkout_error');
      if (checkoutWindow && !checkoutWindow.closed) checkoutWindow.close();
      showError(`${error.message} Es wurde keine Bestellung ausgelöst.`);
      button.disabled = false;
      button.textContent = originalText;
    }
  }

  async function requestBackendPdf() {
    const savePdfResponse = async (pdfResponse, preferredFilename = '') => {
      if (!pdfResponse.ok) throw new Error(`PDF-Download antwortet mit Status ${pdfResponse.status}`);
      const blob = await pdfResponse.blob();
      const disposition = pdfResponse.headers.get('content-disposition') || '';
      const filename = preferredFilename || disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)/i)?.[1] || `Naturstein-Tilmes-Angebot-${Date.now()}.pdf`;
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a'); anchor.href = url; anchor.download = decodeURIComponent(filename); anchor.hidden = true;
      document.body.appendChild(anchor); anchor.click(); anchor.remove(); setTimeout(() => URL.revokeObjectURL(url), 30000);
    };
    const response = await fetch(`${config.backendUrl}${config.pdfEndpoint}`, {
      method: 'POST', headers: {'Content-Type':'application/json', 'Accept':'application/pdf, application/json'}, body: JSON.stringify(backendOfferPayload()),
    });
    if (!response.ok) throw new Error(`PDF-Server antwortet mit Status ${response.status}`);
    if ((response.headers.get('content-type') || '').includes('application/json')) {
      const result = await response.json();
      const downloadUrl = result.downloadUrl || result.download_url || result.url;
      if (!downloadUrl || !/^https:\/\//i.test(downloadUrl)) throw new Error('Der PDF-Server hat keine sichere HTTPS-Download-URL geliefert.');
      const filename = result.offerNumber ? `Naturstein-Tilmes-Angebot-${result.offerNumber}.pdf` : '';
      await savePdfResponse(await fetch(downloadUrl, {headers: {'Accept':'application/pdf'}}), filename);
      return;
    }
    await savePdfResponse(response);
  }

  async function createPdf() {
    if (!validateStep(5)) return;
    const button = root.querySelector('[data-action="pdf"]'); button.disabled = true; button.textContent = 'PDF wird erstellt …';
    try {
      if (state.transportLoading) await state.transportPromise;
      if (config.backendPdf) { await requestBackendPdf(); return; }
      if (window.Shopify?.designMode) throw new Error('Der Shopify Theme-Editor blockiert lokale PDF-Downloads. Öffnen Sie die Theme-Vorschau in einem neuen Tab. Für Downloads direkt im Editor kann nach Bereitstellung des Cloud-Endpunkts „PDF über Angebotsserver erstellen“ aktiviert werden.');
      if (!window.PDFLib) throw new Error('Die mitgelieferte PDF-Bibliothek wurde nicht geladen.');
      const { PDFDocument, StandardFonts, rgb } = window.PDFLib;
      const pdf = await PDFDocument.create(); const regular = await pdf.embedFont(StandardFonts.Helvetica); const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
      const ink = rgb(.09,.14,.11); const green = rgb(.09,.24,.18); const grey = rgb(.42,.45,.43); let pageNumber = 1; let page = pdf.addPage([595.28,841.89]); let y = 790;
      const draw = (text,x,size,font=regular,color=ink) => page.drawText(String(text),{x,y,size,font,color});
      const drawFooter = (targetPage, number) => {
        targetPage.drawLine({start:{x:56,y:70},end:{x:539,y:70},thickness:.5,color:grey});
        ['Pettoo UG','Adenauerallee 33','20097, Hamburg','HRB: 189045'].forEach((text,index)=>targetPage.drawText(text,{x:56,y:56-index*10,size:6.5,font:regular,color:grey}));
        ['Telefon: 0176 43380383','Mail: elias.tilmes@naturstein-tilmes.de'].forEach((text,index)=>targetPage.drawText(text,{x:215,y:56-index*10,size:6.5,font:regular,color:grey}));
        ['BIC: QNTODEB2XXX','IBAN: DE15 1001 0123 8837 4537 70','USt-Id: DE:454600511'].forEach((text,index)=>targetPage.drawText(text,{x:405,y:56-index*10,size:6.5,font:regular,color:grey}));
        targetPage.drawText(`Seite ${number}`,{x:505,y:26,size:6.5,font:regular,color:grey});
      };
      const newPage = () => { pageNumber += 1; page = pdf.addPage([595.28,841.89]); drawFooter(page,pageNumber); y = 790; };
      drawFooter(page,pageNumber);
      const wrapText = (text, maxCharacters = 48) => {
        const words = String(text).split(' '); const lines = []; let line = '';
        for (const word of words) {
          if (line && `${line} ${word}`.length > maxCharacters) { lines.push(line); line = word; }
          else line += `${line ? ' ' : ''}${word}`;
        }
        if (line) lines.push(line);
        return lines;
      };
      draw('Naturstein Tilmes',56,22,bold,green); draw('UNVERBINDLICHES ANGEBOT',330,11,bold,grey); y-=42;
      const offerNo = `NT-${new Date().toISOString().slice(0,10).replaceAll('-','')}-${Math.floor(1000+Math.random()*9000)}`;
      draw(`Angebot: ${offerNo}`,56,10,bold); draw(`Datum: ${new Date().toLocaleDateString('de-DE')}`,390,10); y-=35;
      draw(`${value('firstName')} ${value('lastName')}`,56,10,bold); y-=15; if(value('company')){draw(value('company'),56,10);y-=15;} draw(value('address1'),56,10);y-=15;draw(`${value('postalCode')} ${value('city')}, ${value('countryCode')}`,56,10);y-=32;
      for(const line of wrapText('Vielen Dank für Ihre Anfrage! Auf Grundlage unserer AGBs unterbreiten wir Ihnen folgendes unverbindliches Angebot:',95)){draw(line,56,9);y-=12;}y-=13;
      draw('Pos.',40,8,bold);draw('Menge',76,8,bold);draw('Beschreibung',145,8,bold);draw('Einzel netto',420,8,bold);draw('Gesamt netto',500,8,bold);y-=13;
      for(const line of offerLines()){
        const descriptionLines = wrapText(line.description, 47).slice(0, 3);
        if(y < 105 + descriptionLines.length * 11){newPage();}
        const rowY = y;
        draw(line.position,40,8);draw(line.quantity,76,8);draw(line.unit,420,8);draw(line.total,500,8);
        descriptionLines.forEach((descriptionLine, index) => page.drawText(descriptionLine,{x:145,y:rowY-(index*10),size:8,font:regular,color:ink}));
        y-=Math.max(18,descriptionLines.length*10+6);
      }
      y-=15; const costs=pricing();
      draw('Nettosumme',330,9,bold);draw(costs.hasUnpriced?'teilweise auf Anfrage':currency(costs.totalNetCents),455,9,bold,green);y-=15;
      draw('Umsatzsteuer 19 %',330,9,bold);draw(costs.hasUnpriced?'offen':currency(costs.vatCents),455,9,bold,green);y-=15;
      draw('Gesamtsumme brutto',330,10,bold);draw(costs.hasUnpriced?'Position auf Anfrage':activeTransportQuote()?currency(costs.totalGrossCents):`${currency(costs.totalGrossCents)} zzgl. Transport`,455,10,bold,green);y-=38;
      const paymentText=costs.hasUnpriced
        ? 'Zahlungsbedingung: 100 % Anzahlung der final bestätigten Auftragssumme zahlbar in Vorkasse per Überweisung oder Kreditkarte.'
        : `Zahlungsbedingung: 100 % Anzahlung (${currency(costs.totalGrossCents)} brutto) zahlbar in Vorkasse per Überweisung oder Kreditkarte.`;
      const notes=[
        'Lieferbedingung: DAP, Lieferzeit beträgt voraussichtlich 4-8 Wochen.',
        `Lieferadresse: ${value('address1')}, ${value('postalCode')} ${value('city')}, ${value('countryCode')}.`,
        paymentText,
        ...(checked('installer')?['Fliesenlegerarbeiten wurden unverbindlich angefragt; Preis nach gesonderter Prüfung.']:[]),
        'Dieses Dokument ist ein Angebot, für eine noch zu erbringende Leistung. Dieses Angebot berechtigt nicht zum Vorsteuerabzug. Wir weisen darauf hin, dass eine Rücknahme von ausgelieferten Natursteinen ausgeschlossen ist.',
        'Angebotsgültigkeit: Dieses Angebot ist freibleibend und 30 Tage nach Erhalt gültig. Ein Vertrag kommt erst mit unserer Auftragsbestätigung oder Annahme der vereinbarten Zahlung zustande. Bis zum Vertragsschluss bleiben Zwischenverkauf, Rücktritt und Verfügbarkeit vorbehalten.',
        'Freiwillige Rücknahme: Eine freiwillige Rücknahme mangelfreier Ware erfolgt nur nach vorheriger Zustimmung. Originalverpackte Standardware kann im Einzelfall gegen Übernahme sämtlicher Rücktransportkosten und einer vorab vereinbarten Rücknahmegebühr zurückgenommen werden. Sonderanfertigungen und Sonderformate sind von einer freiwilligen Rücknahme ausgeschlossen. Gesetzliche Mängel-, Rücktritts- und gegebenenfalls bestehende Widerrufsrechte bleiben unberührt.',
        'Wir hoffen, dass Ihnen unser Angebot zusagt, und stehen Ihnen bei Rückfragen zur Verfügung.',
        'Mit freundlichen Grüßen',
        'Elias Tilmes',
        'Naturstein Tilmes'
      ];
      for(const note of notes){const lines=wrapText(note,90);for(const line of lines){if(y<95)newPage();draw(line,56,8);y-=11;}y-=7;}
      const bytes=await pdf.save(); const blob=new Blob([bytes],{type:'application/pdf'}); const url=URL.createObjectURL(blob);
      const anchor=document.createElement('a');anchor.href=url;anchor.download=`Naturstein-Tilmes-Angebot-${offerNo}.pdf`;anchor.style.display='none';document.body.appendChild(anchor);anchor.click();anchor.remove();setTimeout(()=>URL.revokeObjectURL(url),30000);
    } catch(error) { showError(`PDF konnte nicht erstellt werden: ${error.message}`); }
    finally { button.disabled=false;button.textContent='PDF-Angebot'; }
  }

  // Prevent an implicit form submission from reloading the advisor.
  form.addEventListener('submit', (event) => event.preventDefault());
  root.querySelector('[data-field="purchaseMode"]').addEventListener('click', (event) => {
    const button = event.target.closest('button[data-value]');
    if (!button || button.disabled || button.dataset.value === state.purchaseMode) return;
    state.purchaseMode = button.dataset.value;
    trackAdvisorEvent('mb_purchase_mode_selected', { mb_selected_purchase_mode: state.purchaseMode });
    state.surface = state.purchaseMode === 'direct' ? 'Poliert' : '';
    state.selected.clear();
    invalidateTransportQuote({render: false});
    configurePurchaseMode();
    renderProducts();
  });
  root.querySelector('[data-field="surface"]').addEventListener('click', (event) => {
    const button = event.target.closest('button[data-value]'); if (!button) return;
    state.surface = button.dataset.value;
    root.querySelectorAll('[data-field="surface"] button').forEach((item) => item.classList.toggle('is-selected', item === button));
    invalidateTransportQuote({render: false});
  });
  root.querySelector('[data-field="tableShape"]').addEventListener('click', (event) => {
    const button = event.target.closest('button[data-value]'); if (!button) return;
    state.tableShape = button.dataset.value;
    root.querySelectorAll('[data-field="tableShape"] button').forEach((item) => item.classList.toggle('is-selected', item === button));
    updateShapePanels(); updateMetrics();
  });
  root.querySelector('[data-field="worktopShape"]').addEventListener('click', (event) => {
    const button = event.target.closest('button[data-value]'); if (!button) return;
    state.worktopShape = button.dataset.value;
    root.querySelectorAll('[data-field="worktopShape"] button').forEach((item) => item.classList.toggle('is-selected', item === button));
    updateShapePanels(); updateMetrics();
  });
  root.querySelector('[data-field="cutoutShape"]').addEventListener('click', (event) => {
    const button = event.target.closest('button[data-value]'); if (!button) return;
    state.cutoutShape = button.dataset.value;
    root.querySelectorAll('[data-field="cutoutShape"] button').forEach((item) => item.classList.toggle('is-selected', item === button));
    updateShapePanels();
    updateMetrics();
  });
  productContainer.addEventListener('change', (event) => {
    const card = event.target.closest('[data-product-id]'); if (!card || event.target.tagName !== 'SELECT') return;
    if (state.selected.has(card.dataset.productId)) state.selected.set(card.dataset.productId, event.target.value);
    invalidateTransportQuote({render: false});
  });
  productContainer.addEventListener('click', (event) => {
    const button = event.target.closest('button'); const card = event.target.closest('[data-product-id]'); if (!button || !card) return;
    if (button.disabled) return;
    state.selected.clear();
    state.selected.set(card.dataset.productId, card.querySelector('select').value);
    invalidateTransportQuote({render: false});
    const selectedProduct = (data.products || []).find((product) => String(product.id) === String(card.dataset.productId));
    trackAdvisorEvent('mb_product_selected', { mb_product_id: String(card.dataset.productId), mb_product_title: selectedProduct?.title || '' });
    renderProducts();
    setStep(3);
  });
  form.addEventListener('input', (event) => {
    if (['area','waste','thickness','lengthCm','widthCm','cutoutLengthCm','cutoutWidthCm','cutoutRadiusCm','tableLengthCm','tableWidthCm','tableRadiusCm','worktopLengthCm','worktopDepthCm','worktopLegACm','worktopDepthACm','worktopLegBCm','worktopDepthBCm','worktopBackCm','worktopLeftCm','worktopRightCm','worktopUDepthCm'].includes(event.target.name)) updateMetrics();
    if (['postalCode','countryCode'].includes(event.target.name)) invalidateTransportQuote();
  });
  form.elements.color.addEventListener('change', renderProducts); form.elements['product-search'].addEventListener('input', renderProducts);
  form.elements.sample.addEventListener('change', () => invalidateTransportQuote());
  form.elements.installer.addEventListener('change', renderSummary);
  root.querySelector('[data-action="confirm-execution"]').addEventListener('click', () => {
    if (validateStep(3)) {
      trackAdvisorEvent('mb_execution_completed', { mb_surface: state.surface || '' });
      setStep(4);
    } else {
      trackAdvisorEvent('mb_validation_error', { mb_validation_step: 3 });
    }
  });
  root.querySelector('[data-action="confirm-summary"]').addEventListener('click', () => {
    if (!validateStep(3)) {
      trackAdvisorEvent('mb_validation_error', { mb_validation_step: 3, mb_validation_context: 'summary' });
      return;
    }
    if (!validateTransportPostalCode()) {
      trackAdvisorEvent('mb_validation_error', { mb_validation_step: 4, mb_validation_context: 'summary_postal_code' });
      return;
    }
    if (config.liveQuote && !activeTransportQuote()) {
      showError('Bitte ermitteln Sie zuerst den Transportpreis für die aktuelle Zusammenstellung und Postleitzahl.');
      trackAdvisorEvent('mb_validation_error', { mb_validation_step: 4, mb_validation_context: 'summary_transport_quote' });
      return;
    }
    trackAdvisorEvent('mb_summary_completed', { mb_project_count: pricing().projects.length });
    setStep(5);
  });
  root.querySelector('[data-action="back"]').addEventListener('click', () => setStep(state.step - 1));
  root.querySelector('[data-action="quote-transport"]').addEventListener('click', () => requestTransportQuote({force: true}));
  root.querySelector('[data-action="add-product"]').addEventListener('click', () => { trackAdvisorEvent('mb_add_product_click'); addMoreProduct(); });
  root.querySelector('[data-action="pdf"]').addEventListener('click', () => { trackAdvisorEvent('mb_pdf_click'); createPdf(); });
  root.querySelector('[data-action="request"]').addEventListener('click', () => { trackAdvisorEvent('mb_request_click'); submitAdvisorRequest(); });
  root.querySelector('[data-action="checkout"]').addEventListener('click', () => { trackAdvisorEvent('mb_checkout_click'); createDirectCheckout(); });
  updateMetrics(); setStep(firstStep, {focus: false});
});
